import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import config from '../../../../config';

export interface BulkImportResult {
  success: boolean;
  message: string;
  importedCount?: number;
}

export interface BulkPresignResponse {
  upload_url: string;
  fields: Record<string, string>;
  s3_key: string;
  message: string;
}

export const ALLOWED_EXCEL_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

export const ALLOWED_EXCEL_EXTENSIONS = ['.xlsx'];

@Injectable({
  providedIn: 'root',
})
export class BulkImportService {
  private readonly baseUrl = config.API_GATEWAY_ENDPOINT;

  constructor(private http: HttpClient) {}

  /**
   * Validates that the file is an Excel spreadsheet (.xlsx).
   * Checks both the MIME type and the file extension.
   */
  validateExcelFile(file: File): { valid: boolean; error?: string } {
    const extension = file.name
      .substring(file.name.lastIndexOf('.'))
      .toLowerCase();

    const validMime = ALLOWED_EXCEL_MIME_TYPES.includes(file.type);
    const validExt = ALLOWED_EXCEL_EXTENSIONS.includes(extension);

    if (!validMime && !validExt) {
      return {
        valid: false,
        error: 'Only Excel files (.xlsx) are allowed.',
      };
    }

    return { valid: true };
  }

  /**
   * Requests a presigned S3 URL for bulk upload.
   * @param fileName - Original file name (e.g. "ROPA_Template.xlsx")
   */
  presignBulkUpload(fileName: string): Promise<BulkPresignResponse> {
    return lastValueFrom(
      this.http.post<BulkPresignResponse>(
        `${this.baseUrl}/projects/bulk-upload/presign`,
        { file_name: fileName }
      )
    );
  }

  /**
   * Uploads the file directly to S3 using the presigned POST data.
   * All `fields` from the presign response are appended to the FormData
   * before the file, as required by S3 presigned POST policy.
   */
  uploadBulkToS3(file: File, presignResponse: BulkPresignResponse): Promise<void> {
    const formData = new FormData();
    Object.entries(presignResponse.fields).forEach(([key, value]) => {
      formData.append(key, value);
    });
    formData.append('file', file, file.name);
    return lastValueFrom(
      this.http.post<void>(presignResponse.upload_url, formData)
    );
  }

  /**
   * Uploads an Excel file to the bulk-import endpoint.
   * Returns a Promise that resolves with the API response.
   */
  uploadBulkFile(file: File): Promise<BulkImportResult> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    return lastValueFrom(
      this.http.post<BulkImportResult>(
        `${this.baseUrl}/projects/bulk-import`,
        formData
      )
    );
  }

  /**
   * Simulates an upload while the real backend is not available.
   * Randomly resolves or rejects to allow testing both UI paths.
   */
  simulateUpload(_file: File): Promise<BulkImportResult> {
    return new Promise((_resolve, reject) => {
      setTimeout(() => {
        reject(new Error('Simulated bulk import failure.'));
      }, 1500);
    });
  }
}
